/**
 * SPA hooks for the Brands API surface — list, detail, and decision mutation.
 *
 * Never burns Keepa tokens — all reads come from the local brand cache, and
 * the decision mutation only writes the operator decision row.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { BRAND_SIZE_BUCKETS, BRAND_DECISIONS } from '@sourcito/schemas';
import { apiFetch, ApiCallError } from './api';

const BrandSizeBucketSchema = z.enum(BRAND_SIZE_BUCKETS);
const BrandDecisionSchema = z.enum(BRAND_DECISIONS);

/** Single-brand detail — backs BrandSummaryDialog + BrandDetailPage. */
export const BrandDetailsResponse = z.object({
  name: z.string(),
  domain: z.number(),
  status: z.enum(['fresh', 'queued', 'sample_only', 'too_large', 'failed']),
  productCount: z.number().nullable(),
  totalReviewCount: z.number().nullable(),
  avgReviewCount: z.number().nullable(),
  sampledReviewSum: z.number().nullable(),
  sampleSize: z.number(),
  sizeBucket: BrandSizeBucketSchema.nullable(),
  oldestListedAt: z.string().nullable(),
  newestListedAt: z.string().nullable(),
  deepFetchedAt: z.string().nullable(),
  /**
   * Brand-level aggregates from the Keepa searchInsights summary path.
   * Null when the brand has never been summary-enriched. Individual fields
   * inside may still be null when Keepa omitted them (small result sets).
   */
  summary: z
    .object({
      lastSummaryAt: z.string(),
      avgRating: z.number().nullable(),
      sellerCount: z.number().nullable(),
      topSellers: z.record(z.number()).nullable(),
      highestRank: z.number().nullable(),
      lowestRank: z.number().nullable(),
      avgBuyBoxCents: z.number().nullable(),
      avgBuyBox90Cents: z.number().nullable(),
      isFbaPercent: z.number().nullable(),
      soldByAmazonPercent: z.number().nullable(),
      hasCouponPercent: z.number().nullable(),
    })
    .nullable(),
  decision: BrandDecisionSchema.nullable(),
  decisionNotes: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decidedByEmail: z.string().nullable(),
  sizingRationale: z.object({
    bucket: BrandSizeBucketSchema.nullable(),
    observed: z.object({
      productCount: z.number().nullable(),
      totalReviewCount: z.number().nullable(),
      sampledReviewSum: z.number().nullable(),
      sampleSize: z.number(),
    }),
    thresholds: z.record(z.unknown()),
  }),
  recentProducts: z.array(
    z.object({
      asin: z.string(),
      title: z.string().nullable(),
      brand: z.string().nullable(),
      category: z.string().nullable(),
      currentPriceCents: z.number().nullable(),
      currentBsr: z.number().nullable(),
      reviewCount: z.number().nullable(),
      roiGrade: z.enum(['A', 'B', 'C', 'D', 'F']).nullable(),
      estimatedRoiPct: z.string().nullable(),
    })
  ),
  auditHistory: z.array(
    z.object({
      id: z.string(),
      occurredAt: z.string(),
      action: z.string(),
      userEmail: z.string().nullable(),
      changes: z.record(z.unknown()).nullable(),
    })
  ),
});
export type BrandDetailsResponse = z.infer<typeof BrandDetailsResponse>;

/** List row — backs BrandsPage. */
export const BrandListRow = z.object({
  name: z.string(),
  domain: z.number(),
  sizeBucket: BrandSizeBucketSchema.nullable(),
  productCount: z.number().nullable(),
  totalReviewCount: z.number().nullable(),
  /** Summary-path review aggregate. UI falls back to this when totalReviewCount is null. */
  avgReviewCount: z.number().nullable(),
  deepFetchedAt: z.string().nullable(),
  /** Summary-path last-enriched timestamp. UI takes max() with deepFetchedAt. */
  lastSummaryAt: z.string().nullable(),
  decision: BrandDecisionSchema.nullable(),
  decidedAt: z.string().nullable(),
  decisionNotesPreview: z.string().nullable(),
});
export type BrandListRow = z.infer<typeof BrandListRow>;

export const BrandListResponse = z.object({
  rows: z.array(BrandListRow),
  total: z.number(),
  rejectedTotal: z.number(),
  rejectionCapExceeded: z.boolean(),
});
export type BrandListResponse = z.infer<typeof BrandListResponse>;

export interface BrandsListParams {
  decision?: 'selected' | 'rejected' | 'undecided' | 'all';
  size?: ('micro' | 'small' | 'medium' | 'large')[];
  q?: string;
  /** Filter to brands that have ≥1 product in this exact category. */
  category?: string;
  sort?: 'product_count' | 'review_count' | 'decided_at';
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

function buildBrandsQuery(params: BrandsListParams): string {
  const qs = new URLSearchParams();
  if (params.decision && params.decision !== 'all') qs.set('decision', params.decision);
  for (const s of params.size ?? []) qs.append('size', s);
  if (params.q) qs.set('q', params.q);
  if (params.category) qs.set('category', params.category);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  return qs.toString() ? `?${qs.toString()}` : '';
}

export function brandsListQuery(params: BrandsListParams = {}) {
  return queryOptions({
    queryKey: ['brands', 'list', params] as const,
    queryFn: () =>
      apiFetch<BrandListResponse>(`/api/brands${buildBrandsQuery(params)}`, {
        responseSchema: BrandListResponse,
      }),
    staleTime: 15_000,
  });
}

export function useBrandsList(params: BrandsListParams = {}) {
  return useQuery(brandsListQuery(params));
}

export function brandDetailsQuery(name: string | null) {
  return queryOptions({
    queryKey: ['brand', name] as const,
    queryFn: () =>
      apiFetch<BrandDetailsResponse>(`/api/brands/${encodeURIComponent(name!)}`, {
        responseSchema: BrandDetailsResponse,
      }),
    enabled: Boolean(name),
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiCallError && err.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useBrandDetails(name: string | null) {
  return useQuery(brandDetailsQuery(name));
}

export interface BrandDecisionPatchInput {
  name: string;
  domain?: number;
  decision: 'selected' | 'rejected' | null;
  notes?: string;
}

export function useBrandDecisionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandDecisionPatchInput) => {
      const url = `/api/brands/${encodeURIComponent(input.name)}/decision${
        input.domain ? `?domain=${input.domain}` : ''
      }`;
      return apiFetch<{ ok: true; decision: 'selected' | 'rejected' | null; notes: string | null }>(
        url,
        {
          method: 'PATCH',
          body: JSON.stringify({ decision: input.decision, notes: input.notes }),
        }
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['brands', 'list'] });
      qc.invalidateQueries({ queryKey: ['brand', vars.name] });
    },
  });
}
